'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import FABButtonLocation from '@alltrails/amplitude/enums/FABButtonLocation';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logCustomRouteButtonClicked from '@alltrails/amplitude/events/logCustomRouteButtonClicked';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import Edit from '@alltrails/denali/icons/Edit';

const subscribe = () => () => {};

type Props = {
  trailSlug: string;
  trailId: number;
  isMobileOverviewButton?: boolean;
  isDesktopCta?: boolean;
  analyticsLocation: FABButtonLocation;
  trailPageType: TrailPageType;
};

export default function CustomizeRouteButton({ trailSlug, trailId, analyticsLocation, isMobileOverviewButton, isDesktopCta, trailPageType }: Props) {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  // getServerSnapshot returns undefined so the first client render matches the server and avoids a SSR hydration mismatch (React error 418).
  const returnTo = useSyncExternalStore(
    subscribe,
    () => encodeURIComponent(window.location.pathname + window.location.search),
    () => undefined
  );

  const href = `/explore/custom-routes/trail/${trailSlug}${returnTo ? `?returnTo=${returnTo}` : ''}`;

  const onCustomizeRouteClick = useCallback(() => {
    logCustomRouteButtonClicked({ location: analyticsLocation, route_loaded: true, trail_id: trailId, trail_page_type: trailPageType });
  }, [analyticsLocation, trailId, trailPageType]);

  if (isMobileOverviewButton) {
    return <Button linkInfo={{ href }} onClick={onCustomizeRouteClick} text={<FormattedMessage defaultMessage="Customize route" />} width="full" />;
  }

  if (isDesktopCta) {
    return (
      <IconButton
        icon={{ Component: Edit }}
        // IconButton, unlike Button, does not localize linkInfo hrefs.
        linkInfo={{ href: wrapUrlSafe(href, languageRegionCode) }}
        onClick={onCustomizeRouteClick}
        title={intl.formatMessage({ defaultMessage: 'Customize route' })}
      />
    );
  }

  return null;
}
